<template>
  <v-row no-gutters>
    <v-col cols="12">
      <form>
        <v-row no-gutters>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <TextField
              v-model.trim="form.email"
              rules="required|email_without_polish|max:255"
              name="email"
              autocomplete="email"
              inputmode="email"
              :label="$t('label.email')"
              required
            />
            <TextField
              v-model="form.phone_number"
              v-mask="phoneMask"
              rules="required|phoneNumber"
              :label="$t('label.phoneNumber')"
              name="phone"
              autocomplete="tel"
              inputmode="tel"
              required
              type="tel"
            />
            <TextField
              v-model="form.first_name"
              rules="required|name|max:255"
              :name="$t('label.firstName')"
              autocomplete="given-name"
              :label="$t('label.firstName')"
              required
            />
            <TextField
              v-model="form.last_name"
              rules="required|name|max:255"
              :name="$t('label.lastName')"
              autocomplete="family-name"
              :label="$t('label.lastName')"
              required
            />
            <TextArea
              v-model="form.comment"
              name="comment"
              rules="regularField"
              :label="$t('label.formAndTime')"
              :placeholder="$t('label.type')"
              auto-grow
              rows="1"
              flat
            />
          </v-col>
        </v-row>

        <RadioGroup
          v-model="form.category"
          :labels="groupLabels"
          name="category"
          :label="$t('label.whatGroup')"
          rules="required"
          required
          class="mt-0"
          label-font-weight="medium"
        />

        <CRZ
          v-model="form.consents"
          :fields="consentConfig.fieldsets"
        />

        <CaptchaField
          v-if="captchaRequired"
          v-model="form.captchaToken"
          rules="required"
          name="captcha"
          required
        />

        <v-btn
          id="swr-step3-form-submit"
          color="szRed"
          :disabled="getLoader"
          :loading="getLoader"
          class="primary-button mt-5"
          variant="flat"
          :block="$vuetify.display.xs"
          @click="onSubmit"
        >
          {{ $t('family.confirmFamilyChoose') }}
        </v-btn>
        <div class="crz-container mt-5">
          <ExpandableText
            v-for="(field, index) in crzTextFields"
            :key="index"
            :text="field.options.label"
            :additional-text="field.options.details"
          />
        </div>
      </form>
    </v-col>
    <ModalCustom v-model="isFamilyNotAvailable">
      <template #header>
        <p
          v-if="donorResponse.isFamilyChosen"
          class="f-24 mb-2 font-weight-medium"
        >
          {{ $t('family.chosenFamily') }}
        </p>
        <p
          v-else
          class="f-24 mb-2 font-weight-medium"
        >
          {{ $t('family.otherLocation') }}
        </p>
        <p
          v-if="donorResponse.isFamilyChosen"
          class="f-18 mb-0"
        >
          {{ $t('family.chooseOther') }}
        </p>
        <router-link
          :to="{ name: returnLink.toName, params: returnLink.params }"
          class="primary-button d-block d-md-inline-block pl-0 mt-5 mb-3 text-center"
        >
          {{ $t('label.findOtherFamily') }}
        </router-link>
      </template>
      <template #footer>
        <p class="mb-0">
          {{ $t('family.donateForSzp') }}
        </p>
        <a
          :href="szpPaymentLink"
          target="_blank"
          class="secondary-button d-inline-block pl-0 mt-5 mb-3"
        >
          {{ $t('label.donateForSzp') }}
        </a>
      </template>
    </ModalCustom>
  </v-row>
</template>
<script setup lang="ts">
/* eslint-disable camelcase */
import CaptchaField from '@/components/FamilyChoiceForm/CaptchaField.vue';
import CRZ from '@/components/widgets/CRZ.vue';
import RadioGroup from '@/components/widgets/Inputs/RadioGroup.vue';
import TextArea from '@/components/widgets/Inputs/TextArea.vue';
import TextField from '@/components/widgets/Inputs/TextField.vue';
import ModalCustom from '@/components/widgets/ModalCustom.vue';
import { LINKS } from '@/constants/externalLinks';
import { createFamilyChosenEvent, createFormErrorsEvent, createFormSubmitEvent } from '@/plugins/gtm/events';
import { useConsentStore } from '@/stores/Consent';
import { useDonorStore } from '@/stores/Donor';
import type { PersonalDataForm, PersonalDataPreparedForBackend } from '@/types';
import { useGtm } from '@gtm-support/vue-gtm';
import axios from 'axios';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
// @ts-ignore
import { mask } from 'vue-the-mask';
import { useGoTo } from 'vuetify';
import { useStore } from 'vuex';
import ExpandableText from '@/components/widgets/ExpandableText.vue';
import { handleSentryError } from '@/utils/handleSentryError';
import type { GenericObject, InvalidSubmissionContext } from 'vee-validate';
import ROUTE_NAMES from '@/constants/routeNames';
type Props = {
  warehouseId: number;
  captchaRequired: boolean;
};

const props = defineProps<Props>();

const goTo = useGoTo();
const gtm = useGtm();
// vuex
const store = useStore();
const router = useRouter();
const route = useRoute();
const vMask = mask;

const donorStore = useDonorStore();

const phoneMask = {
    mask: 'FFF FFF FFF FFF FFF FFF FF',
    tokens: {
        F: {
            pattern: /\+|[0-9]/
        }
    }
};

const szpPaymentLink: string = LINKS.szpPaymentForm;

const form = ref<PersonalDataForm>({
    email: '',
    phone_number: '',
    first_name: '',
    last_name: '',
    comment: '',
    category: null,
    consents: {},
    captchaToken: null
});

const { handleSubmit } = useForm();

const prepareFormDataForSubmit = (form: PersonalDataForm): PersonalDataPreparedForBackend => {
    const { captchaToken, phone_number, ...rest } = form;

    const formData: PersonalDataPreparedForBackend = {
        ...rest,
        ...(captchaToken && { 'g-recaptcha-response': captchaToken }),
        phone_number: phone_number?.replace(/\s+/g, '')
    };

    return formData;
};

const onValidSubmit = async (): Promise<void> => {
    const preparedFormData: PersonalDataPreparedForBackend = prepareFormDataForSubmit(form.value);
    const familyEditionId: string | string[] = route.params.familyEditionId;
    const warehouseId: number = props.warehouseId;

    try {
        const donor = await store.dispatch('donor/postDonor', {
            familyEditionId,
            warehouseId,
            data: preparedFormData
        });

        donorStore.setDonor(donor);

        const gtmEventValue = {
            familyEditionId,
            warehouseId
        };
        if (!gtm) {
            handleSentryError(new Error('GTM not initialized'));
        } else {
            gtm.trackEvent(createFormSubmitEvent(gtmEventValue));
            gtm.trackEvent(createFamilyChosenEvent(gtmEventValue));
        }
        router.push({ name: ROUTE_NAMES.THANKS });
    } catch (error) {
        if (error instanceof Error) handleSentryError(error);
        const isAxiosError = axios.isAxiosError(error);
        if (isAxiosError && error.response?.status === 404) {
            donorResponse.value.isWarehouseValid = error.response.data.data.isWarehouseValid;
            donorResponse.value.isFamilyChosen = error.response.data.data.isFamilyChoosen;
        }
    }
};

const onInvalidSubmit = ({ errors }: InvalidSubmissionContext<GenericObject, GenericObject>): void => {
    if (!gtm) {
        handleSentryError(new Error('GTM not initialized'));
    } else {
        gtm.trackEvent(createFormErrorsEvent(errors));
    }
    const errorTextElement = document.querySelector<HTMLDivElement>('div.v-input--error');
    if (!errorTextElement) return;
    goTo(errorTextElement, {
        easing: 'easeInQuad'
    });
};
const onSubmit = handleSubmit(onValidSubmit, onInvalidSubmit);

const isFamilyNotAvailable = ref<boolean>(false);

const donorResponse = ref({
    isWarehouseValid: true,
    isFamilyChosen: false
});

watch(donorResponse, (value) => {
    isFamilyNotAvailable.value = value.isWarehouseValid === false || value.isFamilyChosen === true;
}, { deep: true });

const consentStore = useConsentStore();

const getLoader = computed(() => store.getters['donor/getLoader']);
const bubbleId = computed(() => store.getters['families/getBubbleId']);
const consentConfig = computed(() => consentStore.consent ?? { fieldsets: [] });
const getGiftgiverCategories = computed(() => consentStore.giftgiverCategories);
const groupLabels = computed(() => getGiftgiverCategories.value?.map((giftGiverCategory) => ({
    label: giftGiverCategory?.description,
    value: giftGiverCategory?.id
})));
const returnLink = computed(() => ({
    toName: bubbleId.value ? ROUTE_NAMES.FAMILIES_LIST : ROUTE_NAMES.HOME,
    params: bubbleId.value ? { bubbleId: bubbleId.value } : {}
}));
const crzTextFields = computed(() => consentStore.getSplittedLabelTextFields);
</script>
